import { useForm, useFieldArray, Controller } from "react-hook-form";
import { MathEditor } from "./MathEditor";
import { hasIncompleteFormula } from "./MathText";
import { QuizImageButton, QuizImagePreview, QuizImageValue, useQuizImageUpload } from "./QuizImage";
import './CreateQuiz.scss';
import images from "../../assets/images";
import { FC, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { FETCH_POLICY_NO_CACHE, messages, patterns, theme } from "../../utils/constants";
import { Dialog, ThemeProvider } from '@mui/material';
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useAppSelector } from "../../redux/app/hooks";
import { useMutation } from "@apollo/client";
import { CREATE_QUIZ, EDIT_QUIZ } from "../../pages/Quiz/api/QuizQuery";
import ToasterWithMessage from "../toaster";
import { questions, QuizData } from "../../pages/Quiz/Quiz";
import { getScreenDimensions } from "../../utils/helper";
import ConfirmationPopup from "../ConfirmationPopup/ConfirmationPopup";
import { trackButtonClick } from "../../firebase/analyticsHelper";
import { ButtonClickEvents } from "../../firebase/constants/events";

type CreateQuizType = {
    setShowCreate: (val: boolean) => void;
    setIsEdit: (val: boolean) => void;
    setSelectedQuiz: (val: QuizData | undefined) => void;
    isEdit: boolean;
    selectedQuiz: QuizData | undefined;
}

type Option = {
    value: string;
    isCorrect: boolean;
    image?: QuizImageValue | null;
};

type Quiz = {
    question: string;
    questionImage?: QuizImageValue | null;
    options: Option[];
};

type FormValues = {
    quiz: Quiz[];
};

type QuizImagePath = `quiz.${number}.questionImage` | `quiz.${number}.options.${number}.image`;

// How many images are uploaded at the same time when the quiz is saved
const IMAGE_UPLOAD_CONCURRENCY = 4;

const schema = yup.object().shape({
    quiz: yup.array().of(
      yup.object().shape({
        question: yup.string().required(messages.question).matches(patterns.startingWithSpaces, messages.validQuestion).trim()
            .test('complete-formula', messages.incompleteFormula, (value) => !hasIncompleteFormula(value)),
        questionImage: yup.mixed<QuizImageValue>().nullable(),
        options: yup.array().of(
          yup.object().shape({
            value: yup.string().required(messages.option).trim().matches(patterns.startingWithSpaces, messages.validOption)
                .test('complete-formula', messages.incompleteFormula, (value) => !hasIncompleteFormula(value)),
            isCorrect: yup.boolean().required(),
            image: yup.mixed<QuizImageValue>().nullable()
          })
        ).required(),
      })
    ).required()
  });

const inititalValue: Quiz = { 
    question: "", 
    options: [{ value: "", isCorrect: true }, { value: "", isCorrect: false }] 
};

const CreateQuiz: FC<CreateQuizType> = ({ selectedQuiz, isEdit, setShowCreate, setIsEdit, setSelectedQuiz }) => {
    const { closeIcon, DeleteQuiz, quizIcon } = images;
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [isDone, setIsDone] = useState<boolean>(false);
    const { control, handleSubmit, reset, watch, getValues, setValue, formState : {errors} } = useForm<FormValues>({ defaultValues: {quiz: [inititalValue]}, resolver: yupResolver(schema)});
    const [uploadingImages, setUploadingImages] = useState<boolean>(false);
    const { uploadImage } = useQuizImageUpload();
    const { fields, append, remove, update } = useFieldArray({ control, name: "quiz" });
    const [ quizName, setQuizName ] = useState<string | null>();
    const [ durationTime, setDurationTime ] = useState<dayjs.Dayjs | null>(null);
    const [ hasTimeLimit, setHasTimeLimit ] = useState<boolean>(true);
    const selectedSubjectId = localStorage.getItem('selectedSId');
    const selectedClassRoomId = useAppSelector((state) => state.auth.selectedClassRoomId);
    const [validationError, setValidationError] = useState<string | null>();
    const [durationError, setDurationError] = useState<string | null>();
    const { success, error } = ToasterWithMessage();
    const [openReminder, setOpenReminder] = useState<boolean>(false);

    const getDurationSeconds = (dTime: dayjs.Dayjs | null, isEnabled: boolean): number | null => {
        if (!isEnabled || !dTime?.isValid()) return null;
        const hrs = dTime.hour();
        const mins = dTime.minute();
        const total = (hrs * 3600) + (mins * 60);
        return total >= 300 ? total : null;
    };

    const validateDuration = (dTime: dayjs.Dayjs | null, isEnabled: boolean): string | null => {
        if (!isEnabled) return null;
        if (!dTime) {
            return "Duration is required when time limit is enabled";
        }
        if (!dTime.isValid()) {
            return "Please enter a valid time duration (HH:mm)";
        }
        const hrs = dTime.hour();
        const mins = dTime.minute();
        const totalSecs = (hrs * 3600) + (mins * 60);
        if (totalSecs < 300) {
            return "Minimum duration is 5 minutes (00:05)";
        }
        return null;
    };

    const handleTimeLimitToggle = (enabled: boolean) => {
        setHasTimeLimit(enabled);
        if (!enabled) {
            setDurationTime(null);
            setDurationError(null);
        } else {
            setDurationTime(null);
            if (isDone) {
                setDurationError(validateDuration(null, true));
            }
        }
    };

    const [createQuiz,{loading}] = useMutation(CREATE_QUIZ, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setOpenPopup(false);
            setShowCreate(false);
            success(data?.createNewQuiz?.message);
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    })

    const [editQuiz,{loading: EditLoading}] = useMutation(EDIT_QUIZ, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setOpenPopup(false);
            setShowCreate(false);
            setIsEdit(false);
            setSelectedQuiz(undefined);
            success(data?.editQuiz);
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    })

    const handleRemove = (index: number) => {
        if (fields.length > 1) {
            remove(index);
        }
    }
    
    const handleAdd = () => {
        append(inititalValue);
    }

    const handleAddOption = (index: number) => {
        const currentQuiz = getValues(`quiz.${index}`);
        if (currentQuiz.options.length < 6) {
            const updatedOptions = [...currentQuiz.options, { value: "", isCorrect: false }];
            update(index, { ...currentQuiz, options: updatedOptions });
        }
    }

    const handleRemoveOption = (index: number, optionIndex: number) => {
        const currentQuiz = getValues(`quiz.${index}`);
        const updatedOptions = [...currentQuiz.options];
        updatedOptions.splice(optionIndex,1);
        update(index, { ...currentQuiz, options: updatedOptions });
    }

    // Uploads every newly picked image, a few at a time, and swaps each File in the form for its S3 key.
    // Images that already uploaded keep their key, so a retry after a failure only uploads the rest.
    const uploadPendingImages = async (): Promise<boolean> => {
        const pending: { path: QuizImagePath; file: File }[] = [];
        getValues('quiz').forEach((quiz, index) => {
            if (quiz.questionImage instanceof File) {
                pending.push({ path: `quiz.${index}.questionImage`, file: quiz.questionImage });
            }
            quiz.options.forEach((option, optionIndex) => {
                if (option.image instanceof File) {
                    pending.push({ path: `quiz.${index}.options.${optionIndex}.image`, file: option.image });
                }
            });
        });
        if (pending.length === 0) return true;

        setUploadingImages(true);
        let failed = false;
        let next = 0;
        const uploadNext = async () => {
            while (next < pending.length) {
                const { path, file } = pending[next++];
                try {
                    setValue(path, await uploadImage(file));
                } catch {
                    failed = true;
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(IMAGE_UPLOAD_CONCURRENCY, pending.length) }, uploadNext));
        setUploadingImages(false);

        if (failed) {
            error(messages.quizImageUploadFailed);
        }
        return !failed;
    }

    const onSubmit = () => {
        trackButtonClick(ButtonClickEvents?.QUIZ_SUBMIT, {
            description : "User clicked submit button in create/edit quiz",
            page: isEdit ? 'Edit Quiz' : 'Create Quiz'
        });
        setOpenReminder(true);
    }

    const onYesReminder = () => {
        setOpenReminder(false);
        setOpenPopup(true);
    }

    const onNoReminder = () => {
        setOpenReminder(false);
    }
    const validateQuizName = (value: string | null | undefined) => {
        if (!value || value === "") {
          return messages.quizName;
        }
        if (!patterns.startingWithSpaces.test(value)) {
            return messages.validQuizName;
        }
        if (!patterns.quizNameLengthRegex.test(value)) {
          return messages.quizNameLength;
        }
        if (!patterns.fileNameRegex.test(value)) {
          return messages.validQuizName;
        }
        return null;
    };

    const handleQuizName = (val: string) => {
        setQuizName(val)
        if (isDone) {
            setValidationError(validateQuizName(val));
        }
    }

    // The save continues once started, so keep the popup open (no outside click / Esc) until it finishes
    const handleCloseSavePopup = () => {
        if (uploadingImages || loading || EditLoading) return;
        setOpenPopup(false);
    }

    const closePopup = async () => {
        if (uploadingImages) return;
        if (!isDone) {
            setIsDone(true);
        }
        const validateError = validateQuizName(quizName);
        const durError = validateDuration(durationTime, hasTimeLimit);
        setValidationError(validateError);
        setDurationError(durError);

        if (!validateError && !durError) {
            if (!(await uploadPendingImages())) return;
            const questions = getValues('quiz')?.map((val: Quiz) => {
                const temp: any = { qs_info: val?.question?.trim() };
                if (typeof val?.questionImage === 'string') {
                    temp["qs_image"] = val.questionImage;
                }
                const opt: any = {};
                const optImages: Record<string, string> = {};
                val?.options?.forEach((option: Option, index: number) => {
                    opt[String.fromCharCode(65 + index)] = option?.value?.trim();
                    if (typeof option?.image === 'string') {
                        optImages[String.fromCharCode(65 + index)] = option.image;
                    }
                    if (option?.isCorrect) {
                        temp["is_correct"] = String.fromCharCode(65 + index);
                    }
                });
                if (Object.keys(optImages).length > 0) {
                    temp["optionImages"] = optImages;
                }
                return { ...temp, options: opt }
            })
            const durSecs = getDurationSeconds(durationTime, hasTimeLimit);
            if (isEdit) {
                editQuiz({
                    variables: {
                        input: {
                            qzId: selectedQuiz?.quizId,
                            cId: selectedClassRoomId,
                            sjId: selectedSubjectId,
                            qz_title: quizName?.trim(),
                            durationSeconds: durSecs,
                            questions
                        }
                    }
                })
            }
            else {
                createQuiz({
                    variables: {
                        input: {
                            cId: selectedClassRoomId,
                            sjId: selectedSubjectId,
                            qz_title: quizName?.trim(),
                            durationSeconds: durSecs,
                            questions
                        }
                    }
                });
            }
        }
    }

    const handleCorrectOptions = (index: number, correctIndex: number) => {
        const currentQuiz = getValues(`quiz.${index}`);
        const updatedOptions = currentQuiz.options.map((option, idx) => ({
            ...option,
            isCorrect: idx === correctIndex
        }));
        update(index, { ...currentQuiz, options: updatedOptions });
    }

    useEffect(() => {
        if (isEdit && selectedQuiz) {
            const quizes = selectedQuiz?.questions?.map((val: questions, index: number) => {
                return {
                    question: val?.qs_info,
                    questionImage: val?.qs_image ?? null,
                    options: Object.values(val?.options)?.map((option: string, optionIndex: number) => {
                        const optionKey = Object.keys(val?.options)?.[optionIndex];
                        return {
                            value: option,
                            isCorrect: optionKey === val?.is_correct,
                            image: val?.optionImages?.[optionKey] ?? null
                        }
                    })
                }              
            })
            reset({
                quiz: quizes
            })
            handleQuizName(selectedQuiz?.quizTitle);
            if (selectedQuiz?.durationSeconds !== undefined && selectedQuiz?.durationSeconds !== null && Number(selectedQuiz.durationSeconds) > 0) {
                const secs = Number(selectedQuiz.durationSeconds);
                const hrs = Math.floor(secs / 3600);
                const mins = Math.floor((secs % 3600) / 60);
                setDurationTime(dayjs().startOf('day').add(hrs, 'hour').add(mins, 'minute'));
                setHasTimeLimit(true);
            } else {
                setHasTimeLimit(false);
                setDurationTime(null);
            }
        }
    }, [isEdit, selectedQuiz])

    return (
        <>
        <div className="CreateQuizMainContainer"> 
            <form id="createQuizForm" className="QuizformContainer" onSubmit={handleSubmit(onSubmit)}>
                {fields.map((item, index) => {
                    return (
                        <div className="mainFormContainer" key={index}>
                            <div className="titleContainer">
                                <div className="title">{`Question ${ index + 1}` }</div>
                                <div className="icCancel" onClick={() => handleRemove(index)}>
                                    <img className="unionIcon" alt="closeIcon" src={closeIcon} />
                                </div>
                            </div>
                            <div className="innerFormContainer" key={item.id}>
                                <div 
                                    className="optionContainer"
                                >
                                    <Controller
                                        control={control}
                                        name={`quiz.${index}.question`}
                                        render={({ field }) => (
                                            <MathEditor
                                                className="quiz_input_question"
                                                placeholder="Type your question here"
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                inputRef={field.ref}
                                                actions={
                                                    <QuizImageButton
                                                        hasImage={!!watch(`quiz.${index}.questionImage`)}
                                                        onSelect={(file) => setValue(`quiz.${index}.questionImage`, file, { shouldDirty: true })}
                                                    />
                                                }
                                            />
                                        )}
                                    />
                                </div>
                                {watch(`quiz.${index}.questionImage`) &&
                                    <QuizImagePreview
                                        image={watch(`quiz.${index}.questionImage`) as QuizImageValue}
                                        onRemove={() => setValue(`quiz.${index}.questionImage`, null, { shouldDirty: true })}
                                    />
                                }
                                {errors?.quiz?.[index]?.question && <span className="error">{errors?.quiz?.[index]?.question?.message}</span>}
                                {
                                    item?.options.map((optionItem: Option, optionIndex: number) => {
                                        return (
                                            <>
                                                <div className="optionContainer" key={optionIndex}>
                                                    <div className="optionIndex">{String.fromCharCode(65 + optionIndex)}</div>
                                                    <Controller
                                                        control={control}
                                                        name={`quiz.${index}.options.${optionIndex}.value`}
                                                        render={({ field }) => (
                                                            <MathEditor
                                                                className="quiz_input_option"
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                onBlur={field.onBlur}
                                                                inputRef={field.ref}
                                                                actions={
                                                                    <QuizImageButton
                                                                        hasImage={!!watch(`quiz.${index}.options.${optionIndex}.image`)}
                                                                        onSelect={(file) => setValue(`quiz.${index}.options.${optionIndex}.image`, file, { shouldDirty: true })}
                                                                    />
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <div 
                                                        className={optionItem?.isCorrect ? "correctOption" : "wrongOption"} 
                                                        onClick={() => handleCorrectOptions(index, optionIndex)} 
                                                    />
                                                    {(item.options.length > 2 && !optionItem?.isCorrect) && 
                                                        <div 
                                                            className={"removeIcon"} 
                                                            onClick={() => handleRemoveOption(index, optionIndex)}
                                                        >
                                                            <span>{"-"}</span>
                                                        </div>
                                                    }
                                                </div>
                                                {watch(`quiz.${index}.options.${optionIndex}.image`) &&
                                                    <QuizImagePreview
                                                        className="optionImagePreview"
                                                        image={watch(`quiz.${index}.options.${optionIndex}.image`) as QuizImageValue}
                                                        onRemove={() => setValue(`quiz.${index}.options.${optionIndex}.image`, null, { shouldDirty: true })}
                                                    />
                                                }
                                                {errors?.quiz?.[index]?.options?.[optionIndex] &&
                                                    <span className="error optionError">{errors?.quiz?.[index]?.options?.[optionIndex]?.value?.message}</span>
                                                }
                                            </>
                                        )
                                    })
                                }
                            </div>
                            {item?.options.length < 6 && <div className="addAnswersContainer">
                           { getScreenDimensions().isMobileScreen && <div className="deltequiz" onClick={() => handleRemove(index)}>
                                    <img className="deleteIcon" alt="deleteIcon" src={DeleteQuiz} />
                                </div>}
                                <span className="addAnswerBtn" onClick={() => handleAddOption(index)}>Add answers +</span>
                            </div>}
                        </div>
                    );
                })}
                <div className="addQuestion" onClick={handleAdd}>Add Question +</div>
            </form>
            <ThemeProvider theme={theme}>
                <Dialog open={openPopup} classes={{ paper: 'saveQuizPaperStyle' }} onClose={handleCloseSavePopup}>
                <form onSubmit={handleSubmit(closePopup)} className='popupContainer'>
                    <div className='messageContainer'>Save Quiz</div>
                    <div className="quizFieldsGroup">
                        <div className="quizFieldLabel">Quiz Title <span className="reqStar">*</span></div>
                        <div className="quizNameContainer">
                            <input type="text" value={quizName || ""} className="quizName" placeholder="Type quiz title name here..." onChange={(e) => handleQuizName(e.target.value)} />
                            {validationError && <span className="error">{validationError}</span>}
                        </div>

                        <div className="quizTimeLimitToggleCtn">
                            <div className="quizFieldLabel" id="quiz-time-limit-label">Time Limit</div>
                            <label className="quizToggleSwitch" aria-label="Time Limit">
                                <input
                                    type="checkbox"
                                    checked={hasTimeLimit}
                                    onChange={(e) => handleTimeLimitToggle(e.target.checked)}
                                    aria-label="Time Limit"
                                />
                                <span className="quizSlider"></span>
                            </label>
                        </div>

                        <div className={`quizDurationPickerWrapper ${hasTimeLimit ? 'show' : ''}`}>
                            <div className="quizDurationPickerCtn">
                                <div className="quizFieldLabel">
                                    Quiz Duration<span className="reqStar">*</span>
                                </div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopTimePicker
                                        ampm={false}
                                        value={durationTime}
                                        onChange={(newValue) => {
                                            setDurationTime(newValue);
                                            if (isDone) {
                                                setDurationError(validateDuration(newValue, true));
                                            }
                                        }}
                                        format="HH:mm"
                                        views={['hours', 'minutes']}
                                        minTime={dayjs().startOf('day').add(5, 'minute')}
                                        slotProps={{
                                            textField: {
                                                className: `quizTimePickerInput ${durationError ? 'inputError' : ''}`,
                                                placeholder: 'hh:mm',
                                                error: !!durationError
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {durationError && <span className="error">{durationError}</span>}
                            </div>
                        </div>
                    </div>
                    <div className="btnContainer">
                        <button type="submit" className='okButtonStyle' disabled={loading || EditLoading || uploadingImages}>
                            {uploadingImages ? 'Uploading...' : 'Done'}
                        </button>
                    </div>
                </form>
                </Dialog>
            </ThemeProvider>
        </div>
        <ConfirmationPopup
            open={openReminder}
            page={`quiz`}
            gifData={quizIcon}
            message={`Please ensure that a correct answer is selected for each question before submitting the quiz.`}
            yesButtonFunctionality={onYesReminder}
            noButtonFunctionality={onNoReminder}
            messageWidth={'300px'}
        />
        </>
    )
}

export default CreateQuiz;
